import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import client from "../../api/client";
import AuthShell from "./AuthShell";

const PHONE_RE = /^[6-9]\d{9}$/;

const RegisterPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    organizationName: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { organizationName, name, email, phone, password, confirmPassword } = form;
    if (!organizationName || !name || !email || !phone || !password) {
      toast.error("All fields are required");
      return;
    }
    if (!PHONE_RE.test(phone.trim())) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    const cleanEmail = email.trim().toLowerCase();
    try {
      await client.post("/auth/register", {
        organizationName: organizationName.trim(),
        name: name.trim(),
        email: cleanEmail,
        phone: phone.trim(),
        password,
      });
      toast.success("Verification code sent to your email");
      // Confirm the email OTP next, then it's queued for approval.
      navigate("/verify-email", { state: { email: cleanEmail } });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell maxWidth={480}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "secondary.main" }}>
          Create your account
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Register your cable network — we'll email a verification code
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Organization / Network name"
              value={form.organizationName}
              onChange={setField("organizationName")}
              fullWidth
              required
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Your name"
              value={form.name}
              onChange={setField("name")}
              fullWidth
              required
              autoComplete="name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={setField("email")}
              fullWidth
              required
              autoComplete="email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Mobile number"
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={setField("phone")}
              fullWidth
              required
              autoComplete="tel"
              inputProps={{ maxLength: 10 }}
              helperText="10-digit mobile"
            />
          </Grid>
        </Grid>

        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          value={form.password}
          onChange={setField("password")}
          fullWidth
          required
          autoComplete="new-password"
          helperText="At least 8 characters"
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((s) => !s)}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm password"
          type={showPassword ? "text" : "password"}
          value={form.confirmPassword}
          onChange={setField("confirmPassword")}
          fullWidth
          required
          autoComplete="new-password"
          sx={{ mb: 2 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ py: 1.25 }}
        >
          {submitting ? "Submitting…" : "Register"}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ textAlign: "center", mt: 3, color: "text.secondary" }}>
        Already registered?{" "}
        <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
          Sign in
        </Link>
      </Typography>
    </AuthShell>
  );
};

export default RegisterPage;
